import React, { useState, useMemo } from "react";
import { Search } from "lucide-react";
import PageHero from "@/components/PageHero";
import ProductCard from "@/components/ProductCard";
import SectionLabel from "@/components/SectionLabel";
import FormWithWhatsApp from "@/components/FormWithWhatsApp";
import {
  PRODUCTS,
  PRODUCT_CATEGORIES,
  SPECIES,
} from "@/data/products";
import { buildWaLink } from "@/lib/whatsapp";
import { BAH_PRODUCT_CATALOGUE_PDF } from "@/lib/assets";

export default function Products() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [species, setSpecies] = useState("all");

  const filtered = useMemo(() => {
    return PRODUCTS.filter((p) => {
      const q = query.trim().toLowerCase();
      const matchesQuery =
        !q ||
        p.name.toLowerCase().includes(q) ||
        (p.active_ingredient || "").toLowerCase().includes(q) ||
        (p.use_summary || "").toLowerCase().includes(q);
      const matchesCategory = category === "all" || p.category === category;
      const matchesSpecies = species === "all" || p.species.includes(species);
      return matchesQuery && matchesCategory && matchesSpecies;
    });
  }, [query, category, species]);

  return (
    <div data-testid="products-page">
      <PageHero
        testId="products-hero"
        eyebrow="Products"
        title="Biodeal Animal Health product range."
        lede="Medicines, supplements, biosecurity and companion-animal products — sourced directly from the BAH Product Catalogue (August 2025)."
        image="https://images.pexels.com/photos/37748139/pexels-photo-37748139.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600"
        primaryCta={{
          label: "Download Catalogue",
          href: BAH_PRODUCT_CATALOGUE_PDF,
          external: true,
        }}
        secondaryCta={{ label: "Veterinary Instruments", to: "/instruments" }}
      />

      {/* Filter bar */}
      <section className="sticky top-16 md:top-20 z-40 bg-bah-paper/95 backdrop-blur border-b border-bah-line" data-testid="products-filter-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-bah-mute" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products, ingredients, indications…"
              className="w-full pl-10 pr-4 py-3 rounded-full border border-bah-line bg-white focus:outline-none focus:border-bah-green focus:ring-2 focus:ring-bah-green/30 text-sm"
              data-testid="products-search"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="products-filter-category"
            className="rounded-full border border-bah-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-bah-green focus:ring-2 focus:ring-bah-green/30"
          >
            <option value="all">All categories</option>
            {PRODUCT_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            value={species}
            onChange={(e) => setSpecies(e.target.value)}
            data-testid="products-filter-species"
            className="rounded-full border border-bah-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-bah-green focus:ring-2 focus:ring-bah-green/30"
          >
            <option value="all">All species</option>
            {SPECIES.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Results */}
      <section className="py-12 md:py-20 bg-bah-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <SectionLabel>Catalogue</SectionLabel>
              <h2 className="mt-3 font-display text-2xl md:text-3xl text-bah-ink">
                {filtered.length} product{filtered.length === 1 ? "" : "s"} matched
              </h2>
            </div>
            {filtered.length === 0 && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setCategory("all");
                  setSpecies("all");
                }}
                className="btn-ghost-dark"
                data-testid="products-clear-filters"
              >
                Clear filters
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white border border-bah-line rounded-2xl p-10 text-center">
              <p className="text-bah-ink font-display text-xl">No products matched those filters.</p>
              <p className="mt-2 text-bah-mute text-sm">Try clearing filters or talk to the technical team.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Product Inquiry */}
      <section className="py-20 md:py-28 bg-bah-paperAlt" data-testid="product-inquiry-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel>Request a quote</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-bah-ink leading-tight">
              Send a product inquiry.
            </h2>
            <p className="mt-4 text-bah-mute">
              Request stock availability, pack sizes, technical clarification
              or a written quote. Form goes to the Biodeal Animal Health
              team — or use WhatsApp for an instant conversation.
            </p>
          </div>
          <div className="lg:col-span-7">
            <FormWithWhatsApp
              endpoint="/forms/product-inquiry"
              testidPrefix="product-inquiry"
              submitLabel="Send Product Inquiry"
              successText="We've received your inquiry. The Biodeal Animal Health team will be in touch shortly."
              aiDraft={{ topic: "product-inquiry", targetField: "message", roleField: "role" }}
              waMessageBuilder={(v) =>
                `Hello Biodeal Animal Health, I am ${v.name || "[name]"} and would like an inquiry on ${
                  v.product_name || "[product]"
                }. ${v.quantity ? `Quantity: ${v.quantity}. ` : ""}${v.message || ""}`
              }
              fields={[
                { name: "name", label: "Your name", required: true, placeholder: "Full name" },
                { name: "phone", label: "Phone / WhatsApp", required: true, placeholder: "+254…" },
                { name: "email", label: "Email", type: "email" },
                {
                  name: "role",
                  label: "Role",
                  options: ["Veterinary professional", "Agrovet", "Farmer", "Distributor", "Institution", "Other"],
                },
                { name: "product_name", label: "Product of interest", required: true, placeholder: "e.g. WORMIZAN SUPER" },
                { name: "quantity", label: "Quantity estimate", placeholder: "e.g. 50 x 1 litre" },
                {
                  name: "message",
                  label: "Message",
                  textarea: true,
                  rows: 4,
                  full: true,
                  placeholder: "Any specifics, timelines, or technical clarifications you need.",
                },
              ]}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
